import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';

/**
 * Helper to create a realtime channel with reconnection handling
 * @param {string} channelName - Unique channel name
 * @param {Function} onReconnect - Callback to refetch data when reconnected
 * @returns {Object} channel - Supabase realtime channel
 */
function createRealtimeChannel(channelName, onReconnect) {
  const channel = supabase.channel(channelName);

  // Track connection status for reconnection handling
  channel.on('system', { event: '*' }, (payload) => {
    console.log(`📡 Realtime [${channelName}] status:`, payload.status);
    if (payload.status === 'SUBSCRIBED') {
      console.log(`✅ Realtime [${channelName}] connected`);
    } else if (payload.status === 'CHANNEL_ERROR') {
      console.error(`❌ Realtime [${channelName}] error - will retry`);
    } else if (payload.status === 'TIMED_OUT') {
      console.warn(`⏰ Realtime [${channelName}] timed out - reconnecting...`);
      // Supabase will auto-reconnect, but we should refetch when it does
    }
  });

  return channel;
}

/**
 * Hook to refetch data when page becomes visible (handles long inactive periods)
 * @param {Function} refetchFn - Function to call when visibility changes to visible
 */
function useVisibilityRefetch(refetchFn) {
  const lastVisibleRef = useRef(Date.now());

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastVisible = Date.now() - lastVisibleRef.current;
        // If page was hidden for more than 30 seconds, refetch data
        if (timeSinceLastVisible > 30000) {
          console.log('🔄 Page visible after long absence, refetching data...');
          refetchFn();
        }
        lastVisibleRef.current = Date.now();
      } else {
        lastVisibleRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refetchFn]);
}

/**
 * Live Jobs List
 * Subscribes to INSERT, UPDATE, DELETE events on jobs table
 */
export function useLiveJobs(filters = {}) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoized load function for reuse
  const load = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('jobs')
        .select('*, employer:profiles!employer_id(id,name,business_name,town,rating,verified)')
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (filters.location && filters.location !== 'all') {
        query = query.eq('location', filters.location);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Jobs load error:', error);
      }
      setJobs(data ?? []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.location]);

  // Refetch when page becomes visible after being hidden
  useVisibilityRefetch(load);

  useEffect(() => {
    load();

    // Live updates with reconnection handling
    const channel = createRealtimeChannel('public-jobs', load)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'jobs' }, (payload) => {
        console.log('🔄 Job INSERTED:', payload.new);
        // For INSERT, we need to fetch with employer details
        supabase
          .from('jobs')
          .select('*, employer:profiles!employer_id(id,name,business_name,town,rating,verified)')
          .eq('id', payload.new.id)
          .single()
          .then(({ data }) => {
            if (data && data.status === 'open') {
              setJobs(prev => [data, ...prev]);
            }
          })
          .catch((err) => console.error('Error fetching inserted job:', err));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'jobs' }, (payload) => {
        console.log('🔄 Job UPDATED:', payload.new);
        setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'jobs' }, (payload) => {
        console.log('🔄 Job DELETED:', payload.old);
        setJobs(prev => prev.filter(j => j.id !== payload.old.id));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [load]);

  return { jobs, loading, setJobs, refetch: load };
}

/**
 * Live Gigs List
 * Subscribes to INSERT, UPDATE, DELETE events on gigs table
 */
export function useLiveGigs(filters = {}) {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoized load function for reuse
  const load = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('gigs')
        .select('*, owner:profiles!owner_id(id,name,business_name,town,rating,verified)')
        .eq('status', 'open')
        .order('created_at', { ascending: false });

      if (filters.location && filters.location !== 'all') {
        query = query.eq('location', filters.location);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Gigs load error:', error);
      }
      setGigs(data ?? []);
    } catch (error) {
      console.error('Failed to load gigs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.location]);

  // Refetch when page becomes visible after being hidden
  useVisibilityRefetch(load);

  useEffect(() => {
    load();

    // Live updates with reconnection handling
    const channel = createRealtimeChannel('public-gigs', load)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'gigs' }, (payload) => {
        console.log('🔄 Gig INSERTED:', payload.new);
        // Fetch with owner details
        supabase
          .from('gigs')
          .select('*, owner:profiles!owner_id(id,name,business_name,town,rating,verified)')
          .eq('id', payload.new.id)
          .single()
          .then(({ data }) => {
            if (data && data.status === 'open') {
              setGigs(prev => [data, ...prev]);
            }
          })
          .catch((err) => console.error('Error fetching inserted gig:', err));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'gigs' }, (payload) => {
        console.log('🔄 Gig UPDATED:', payload.new);
        setGigs(prev => prev.map(g => g.id === payload.new.id ? { ...g, ...payload.new } : g));
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'gigs' }, (payload) => {
        console.log('🔄 Gig DELETED:', payload.old);
        setGigs(prev => prev.filter(g => g.id !== payload.old.id));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [load]);

  return { gigs, loading, setGigs, refetch: load };
}

/**
 * Live Bids for a Specific Gig
 * Subscribes to bids for a specific gig_id
 */
export function useLiveBids(gigId) {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gigId) {
      setLoading(false);
      return;
    }

    // Initial load
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('bids')
          .select('*, contractor:profiles!contractor_id(id,name,business_name,role,town,rating,verified,skills)')
          .eq('gig_id', gigId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Bids load error:', error);
        }
        setBids(data ?? []);
      } catch (error) {
        console.error('Failed to load bids:', error);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Live updates
    const channel = supabase
      .channel(`gig-bids-${gigId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bids', filter: `gig_id=eq.${gigId}` },
        (payload) => {
          console.log('🔄 Bid INSERTED:', payload.new);
          // Fetch with contractor details
          supabase
            .from('bids')
            .select('*, contractor:profiles!contractor_id(id,name,business_name,role,town,rating,verified,skills)')
            .eq('id', payload.new.id)
            .single()
            .then(({ data }) => {
              if (data) {
                setBids(prev => [data, ...prev]);
              }
            })
            .catch((err) => console.error('Error fetching inserted bid:', err));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'bids', filter: `gig_id=eq.${gigId}` },
        (payload) => {
          console.log('🔄 Bid UPDATED:', payload.new);
          setBids(prev => prev.map(b => b.id === payload.new.id ? { ...b, ...payload.new } : b));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'bids', filter: `gig_id=eq.${gigId}` },
        (payload) => {
          console.log('🔄 Bid DELETED:', payload.old);
          setBids(prev => prev.filter(b => b.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [gigId]);

  return { bids, loading, setBids };
}

/**
 * Live Applications for a Specific Job
 * Subscribes to applications for a specific job_id
 */
export function useLiveApplications(jobId) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    // Initial load
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('applications')
          .select('*, seeker:profiles!seeker_id(id,name,business_name,role,town,rating,verified,skills,experience)')
          .eq('job_id', jobId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Applications load error:', error);
        }
        setApplications(data ?? []);
      } catch (error) {
        console.error('Failed to load applications:', error);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Live updates
    const channel = supabase
      .channel(`job-applications-${jobId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'applications', filter: `job_id=eq.${jobId}` },
        (payload) => {
          console.log('🔄 Application INSERTED:', payload.new);
          // Fetch with seeker details
          supabase
            .from('applications')
            .select('*, seeker:profiles!seeker_id(id,name,business_name,role,town,rating,verified,skills,experience)')
            .eq('id', payload.new.id)
            .single()
            .then(({ data }) => {
              if (data) {
                setApplications(prev => [data, ...prev]);
              }
            })
            .catch((err) => console.error('Error fetching inserted application:', err));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'applications', filter: `job_id=eq.${jobId}` },
        (payload) => {
          console.log('🔄 Application UPDATED:', payload.new);
          setApplications(prev => prev.map(a => a.id === payload.new.id ? { ...a, ...payload.new } : a));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'applications', filter: `job_id=eq.${jobId}` },
        (payload) => {
          console.log('🔄 Application DELETED:', payload.old);
          setApplications(prev => prev.filter(a => a.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [jobId]);

  return { applications, loading, setApplications };
}

/**
 * Live User-Specific Jobs (for employer dashboard)
 * Subscribes to jobs for a specific employer_id
 */
export function useLiveUserJobs(employerId) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employerId) {
      setLoading(false);
      return;
    }

    // Initial load
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('id, title, description, location, locations, status, created_at, boosted_at, is_featured, featured_until, post_type, slug, positions_available, expires_at, urgent_hiring, no_experience_needed, with_housing, salary_min, salary_max, required_documents')
          .eq('employer_id', employerId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('User jobs load error:', error);
        }
        setJobs(data ?? []);
      } catch (error) {
        console.error('Failed to load user jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Live updates
    const channel = supabase
      .channel(`user-jobs-${employerId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'jobs', filter: `employer_id=eq.${employerId}` },
        (payload) => {
          console.log('🔄 User Job INSERTED:', payload.new);
          setJobs(prev => [payload.new, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'jobs', filter: `employer_id=eq.${employerId}` },
        (payload) => {
          console.log('🔄 User Job UPDATED:', payload.new);
          setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'jobs', filter: `employer_id=eq.${employerId}` },
        (payload) => {
          console.log('🔄 User Job DELETED:', payload.old);
          setJobs(prev => prev.filter(j => j.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [employerId]);

  return { jobs, loading, setJobs };
}

/**
 * Live User-Specific Gigs (for owner dashboard)
 * Subscribes to gigs for a specific owner_id
 */
export function useLiveUserGigs(ownerId) {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ownerId) {
      setLoading(false);
      return;
    }

    // Initial load
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('gigs')
          .select('id, title, description, location, status, created_at, boosted_at, is_featured, featured_until')
          .eq('owner_id', ownerId)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('User gigs load error:', error);
        }
        setGigs(data ?? []);
      } catch (error) {
        console.error('Failed to load user gigs:', error);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Live updates
    const channel = supabase
      .channel(`user-gigs-${ownerId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'gigs', filter: `owner_id=eq.${ownerId}` },
        (payload) => {
          console.log('🔄 User Gig INSERTED:', payload.new);
          setGigs(prev => [payload.new, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'gigs', filter: `owner_id=eq.${ownerId}` },
        (payload) => {
          console.log('🔄 User Gig UPDATED:', payload.new);
          setGigs(prev => prev.map(g => g.id === payload.new.id ? { ...g, ...payload.new } : g));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'gigs', filter: `owner_id=eq.${ownerId}` },
        (payload) => {
          console.log('🔄 User Gig DELETED:', payload.old);
          setGigs(prev => prev.filter(g => g.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [ownerId]);

  return { gigs, loading, setGigs };
}
